/**
 * Модалка «Сохранить место» — открывается из MapPage когда юзер
 * жмёт «Сохранить это место» в попапе карты. Если юзер не залогинен,
 * MapPage редиректит на /auth?next=…/map вместо открытия модалки.
 *
 * Простой реактовский диалог поверх карты — без портала, без focus
 * trap'а; для one-shot формы достаточно.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { createSpot, fetchForestLegend } from "@mushroom-map/api-client";
import type { SpotRating } from "@mushroom-map/types";
import { useAuth } from "../auth/useAuth";
import { RATING_OPTIONS } from "../lib/spotRating";
import { TREE_TAGS, MUSHROOM_TAGS, BERRY_TAGS, type SpotTag } from "../lib/spotTags";
import { FOREST_COLORS, type ForestSlug } from "../lib/forestStyle";
import { track } from "../lib/track";

// Module-level cache forest/legend (тот же что в Legend.tsx). Один
// fetch на сессию вместо отдельных запросов из обоих компонентов.
let _legendSpeciesCache: Set<string> | null = null;
async function getAvailableTreeSlugs(): Promise<Set<string>> {
  if (_legendSpeciesCache) return _legendSpeciesCache;
  try {
    const d = await fetchForestLegend();
    _legendSpeciesCache = new Set(d.species.map((s) => s.slug));
    return _legendSpeciesCache;
  } catch {
    return new Set(); // fallback на пустой → покажем все TREE_TAGS
  }
}


interface Props {
  lat: number;
  lon: number;
  onClose: () => void;
  /** Вызывается после успешного create — даёт MapPage шанс рефрешнуть
   *  spots-слой на карте. Опционально. */
  onSaved?: () => void;
}


export function SaveSpotModal({ lat, lon, onClose, onSaved }: Props) {
  const { getAccessToken } = useAuth();
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [rating, setRating] = useState<SpotRating>(3);
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // V4.1: после первого нажатия Сохранить с пустым required-полем
  // включаем валидационную подсветку. Раньше кнопка была silently
  // disabled, юзер кликал без эффекта и не понимал почему.
  const [showErrors, setShowErrors] = useState(false);
  // V4.4: фильтруем TREE_TAGS по тому, что реально есть в forest
  // distribution (= по тем же slug'ам что в легенде). При первом
  // mount'е модалки делаем fetch /api/forest/legend; пока pending —
  // показываем full TREE_TAGS. Это синхронизация с тем что юзер видит
  // в Legend.
  const [availableTrees, setAvailableTrees] = useState<Set<string> | null>(
    _legendSpeciesCache,
  );
  useEffect(() => {
    if (availableTrees) return;
    let cancelled = false;
    void getAvailableTreeSlugs().then((set) => {
      if (!cancelled && set.size > 0) setAvailableTrees(set);
    });
    return () => {
      cancelled = true;
    };
  }, [availableTrees]);

  const filteredTreeTags = availableTrees
    ? TREE_TAGS.filter((t) => availableTrees.has(t.slug))
    : TREE_TAGS;

  const toggleTag = (slug: string) => {
    setTags((cur) =>
      cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug],
    );
  };
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNameInvalid = showErrors && name.trim().length === 0;

  // Esc закрывает.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length === 0) {
      setShowErrors(true);
      // Фокус и скролл к первому невалидному полю.
      const el = document.getElementById("save-spot-name");
      if (el) {
        el.focus();
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
    const tok = getAccessToken();
    if (!tok) return;
    setSubmitting(true);
    try {
      await createSpot(tok, {
        name: name.trim(),
        note: note.trim(),
        rating,
        tags,
        lat,
        lon,
      });
      setDone(true);
      setError(null);
      // Аналитика: считаем, сколько спот'ов сохранено + как часто юзер
      // рейтит/тегает. lat/lon/name НЕ трекаем — это PII.
      track("spot.save", { has_rating: rating !== null, tag_count: tags.length });
      onSaved?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Сохранить место"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,20,20,0.4)",
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
        padding: "var(--space-3)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          // V4.6: cream вместо paper-rise — синхрон с InlineSearch
          // dropdown / Spotlight / FeedbackModal / popup'ом карты.
          background:    "var(--cream)",
          color:         "var(--ink)",
          border:        0,
          borderRadius:  "var(--radius-lg)",
          padding:       "var(--space-5)",
          width:         "min(420px, 100%)",
          maxHeight:     "90vh",
          overflowY:     "auto",
          boxShadow:     "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "var(--space-3)" }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "var(--fs-h3)" }}>
            {done ? "Сохранено" : "Сохранить место"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              background: "transparent",
              border:     "none",
              fontSize:   "1.4rem",
              lineHeight: 1,
              cursor:     "pointer",
              color:      "var(--ink-faint)",
              padding:    0,
              width:      28,
              height:     28,
            }}
          >
            ×
          </button>
        </div>

        {done ? (
          <>
            <p style={{ marginTop: 0, color: "var(--ink-dim)", fontSize: "var(--fs-sm)" }}>
              Точка добавлена в ваш кабинет. Координаты:{" "}
              <code>{lat.toFixed(5)}, {lon.toFixed(5)}</code>
            </p>
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end", marginTop: "var(--space-4)" }}>
              <button
                type="button"
                onClick={onClose}
                style={modalBtnStyle("ghost")}
              >
                Закрыть
              </button>
              <Link
                to="/spots"
                style={{ ...modalBtnStyle("primary"), textDecoration: "none" }}
              >
                Все места
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: "var(--space-3)" }} noValidate>
            <label style={fieldLabel}>
              <span style={{ color: isNameInvalid ? "var(--danger)" : "var(--ink-dim)" }}>
                Название{isNameInvalid && " · обязательно"}
              </span>
              <input
                id="save-spot-name"
                type="text"
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Поляна за Лемболово"
                style={isNameInvalid ? { ...inputStyle, ...invalidInputStyle } : inputStyle}
                aria-invalid={isNameInvalid || undefined}
                autoFocus
              />
            </label>

            <label style={fieldLabel}>
              <span>Заметка</span>
              <textarea
                value={note}
                maxLength={4000}
                rows={2}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Что нашли / что вспомнить позже"
                style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--font-body)" }}
              />
            </label>

            <fieldset style={fieldsetStyle}>
              <legend style={legendStyle}>Оценка места</legend>
              <p style={{ fontSize: "var(--fs-xs)", color: "var(--ink-faint)", margin: "0 0 var(--space-2)" }}>
                Насколько хорошее место для сбора (1 = плохое, 5 = отличное).
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "var(--space-1)" }}>
                {RATING_OPTIONS.map((r) => {
                  const isSelected = rating === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRating(r.value)}
                      aria-pressed={isSelected}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                        padding: "var(--space-2) 0",
                        background: isSelected ? r.hex : "var(--paper)",
                        color: isSelected ? "white" : "var(--ink)",
                        border: isSelected ? "2px solid var(--ink)" : "1px solid var(--rule)",
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer",
                        transition: "background 120ms ease, border-color 120ms ease, color 120ms ease",
                        fontFamily: "var(--font-body)",
                        fontSize: "var(--fs-sm)",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{r.value}</span>
                      <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.85 }}>{r.label}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <TagBlock title="Деревья" options={filteredTreeTags} selected={tags} onToggle={toggleTag} colorBySlug />
            <TagBlock title="Грибы"   options={MUSHROOM_TAGS} selected={tags} onToggle={toggleTag} />
            <TagBlock title="Ягоды"   options={BERRY_TAGS} selected={tags} onToggle={toggleTag} />

            <p style={{ fontSize: "var(--fs-xs)", color: "var(--ink-faint)", margin: 0, fontFamily: "var(--font-mono)" }}>
              {lat.toFixed(5)}, {lon.toFixed(5)}
            </p>

            {error && (
              <p style={{ color: "var(--danger)", fontSize: "var(--fs-sm)", margin: 0 }}>
                {error}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
              <button type="button" onClick={onClose} style={modalBtnStyle("ghost")}>
                Отмена
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={modalBtnStyle("primary")}
              >
                {submitting ? "Сохраняем…" : "Сохранить"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// Tag-picker блок: чипы с checkbox-семантикой. Multi-select без verbose UI.
//
// `colorBySlug` (V4.4) — если true, цвет активного чипа берётся из
// `FOREST_COLORS[slug]` (та же палитра что в Legend / forest-fill).
// Деревьям делаем визуально согласованно с легендой.
function TagBlock({ title, options, selected, onToggle, colorBySlug = false }: {
  title: string;
  options: SpotTag[];
  selected: string[];
  onToggle: (slug: string) => void;
  colorBySlug?: boolean;
}) {
  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{title}</legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((t) => {
          const on = selected.includes(t.slug);
          const activeColor = colorBySlug
            ? FOREST_COLORS[t.slug as ForestSlug] ?? "var(--forest)"
            : "var(--forest)";
          return (
            <button
              key={t.slug}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => onToggle(t.slug)}
              style={{
                padding: "4px 10px",
                border: `1px solid ${on ? activeColor : "var(--rule)"}`,
                background: on ? activeColor : "var(--paper)",
                color: on ? "#fff" : "var(--ink)",
                borderRadius: 999,
                fontSize: "var(--fs-sm)",
                fontFamily: "var(--font-body)",
                cursor: "pointer",
                lineHeight: 1.3,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// Inline styles — для one-shot модалки нет смысла заводить отдельный CSS-modul.

const fieldsetStyle: React.CSSProperties = {
  border: "none",
  padding: 0,
  margin: 0,
};
const legendStyle: React.CSSProperties = {
  fontSize: "var(--fs-sm)",
  color: "var(--ink-dim)",
  marginBottom: "var(--space-1)",
  padding: 0,
};

const fieldLabel: React.CSSProperties = {
  display: "grid",
  gap: "var(--space-1)",
  fontSize: "var(--fs-sm)",
};

const inputStyle: React.CSSProperties = {
  font: "inherit",
  fontFamily: "var(--font-body)",
  padding: "var(--space-2) var(--space-3)",
  border: "1px solid var(--rule)",
  borderRadius: 6,
  background: "var(--paper)",
  color: "var(--ink)",
};

// Подсветка required-поля после неуспешной попытки submit'а.
const invalidInputStyle: React.CSSProperties = {
  borderColor: "var(--danger)",
  boxShadow: "0 0 0 3px color-mix(in srgb, var(--danger) 22%, transparent)",
};

function modalBtnStyle(variant: "primary" | "ghost"): React.CSSProperties {
  if (variant === "primary") {
    return {
      padding: "var(--space-2) var(--space-4)",
      background: "var(--forest)",
      color: "#fff",
      border: "1px solid var(--forest)",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: "var(--fs-sm)",
      fontWeight: 500,
      fontFamily: "var(--font-body)",
    };
  }
  return {
    padding: "var(--space-2) var(--space-4)",
    background: "transparent",
    color: "var(--ink-dim)",
    border: "1px solid var(--rule)",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: "var(--fs-sm)",
    fontFamily: "var(--font-body)",
  };
}
